// ============================================================
// VCS.ChatMediaGallery — Medien-Galerie (Fullscreen Overlay)
// - Fullscreen-Viewer fuer Fotos, Sprachbilder, Videos
// - Swipe-Navigation zwischen Medien eines Chats
// - Karaoke-Effekt fuer Sprachbilder im Fullscreen-Modus
// - Download, Weiterleiten, Loeschen aus dem Viewer
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatMediaGallery = class {

    // ── Konstanten ───────────────────────────────────────────

    static TABS = ['image', 'video', 'audio', 'voicephoto'];

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {
            overlay : $('#mediaGalleryOverlay'),
            closeBtn: $('#mediaGalleryClose'),
            peerName: $('#mediaGalleryPeerName'),
            tabBar  : $('#mediaGalleryTabs'),
            grid    : $('#mediaGalleryGrid'),
            empty   : $('#mediaGalleryEmpty'),
        };
        this._media = [];
        this._activeTab = 'image';
        this._activePlayer = null;
        this._karaokeRaf = null;
        this._cachedChatId = null;
        this._cachedMedia = null;
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async open() {
        const chatId = VCS.state.chatActiveChatId;
        if (!chatId) return;

        const peerName = $('#chatDetailPeerName')[0]?.textContent || '';
        this._view.peerName[0].textContent = peerName;

        this._view.overlay.removeClass('hidden');
        this._setActiveTab('image');
        this._view.grid[0].innerHTML = '';
        this._view.empty.addClass('hidden');

        // Medienliste aus Cache oder vom Server laden
        if (this._cachedChatId === chatId && this._cachedMedia) {
            this._media = this._cachedMedia;
        } else {
            try {
                const res = await fetch(VCS.Chat.API_BASE + '/chat-media/' + encodeURIComponent(chatId), {
                    headers: { 'Authorization': 'Bearer ' + VCS.state.chatAuthToken }
                });
                if (!res.ok) throw new Error(res.status);
                this._media = await res.json();
            } catch (e) {
                console.warn('[media-gallery] Fehler beim Laden:', e);
                this._media = [];
            }

            // Transcription-JSON parsen für Karaoke-Wörter
            for (const m of this._media) {
                if (m.transcription && m.mediaType === 'voicephoto') {
                    try {
                        const parsed = JSON.parse(m.transcription);
                        if (Array.isArray(parsed)) {
                            m._vpWords = parsed;
                        } else {
                            m._vpWords = parsed.original || [];
                            if (parsed.translated) {
                                m._vpWordsTranslated = parsed.translated;
                                m._vpTargetLang = parsed.targetLang;
                            }
                        }
                    } catch (_) {}
                }
                if (m.transcription && m.mediaType === 'video') {
                    try {
                        const parsed = JSON.parse(m.transcription);
                        m._videoWords = parsed.original || [];
                        if (parsed.translated) {
                            m._videoWordsTranslated = parsed.translated;
                            m._videoTargetLang = parsed.targetLang;
                        }
                    } catch (_) {}
                }
            }

            this._cachedChatId = chatId;
            this._cachedMedia = this._media;
        }

        this._render();
    }

    /**
     * Invalidiert den Medienlisten-Cache (z.B. nach neuem Medien-Upload).
     */
    invalidateCache(chatId) {
        if (!chatId || chatId === this._cachedChatId) {
            this._cachedChatId = null;
            this._cachedMedia = null;
        }
    }

    close() {
        this._stopActivePlayer();
        this._view.overlay.addClass('hidden');
        this._view.grid[0].innerHTML = '';
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        this._view.closeBtn.on('click', () => this.close());

        this._view.tabBar.on('click', '.media-gallery-tab', (e) => {
            const tab = e.currentTarget.dataset.tab;
            if (tab && tab !== this._activeTab) {
                this._stopActivePlayer();
                this._setActiveTab(tab);
                this._render();
            }
        });

        // Klick auf Chat-Header-Info öffnet Galerie
        $(document).on('click', '.chat-header-info', () => {
            if (VCS.state.chatActiveChatId) {
                this.open();
            }
        });
    }

    // ── Private Methoden ─────────────────────────────────────

    _setActiveTab(tab) {
        this._activeTab = tab;
        this._view.tabBar.find('.media-gallery-tab').each(function () {
            this.classList.toggle('media-gallery-tab--active', this.dataset.tab === tab);
        });
    }

    _render() {
        const grid = this._view.grid[0];
        grid.innerHTML = '';

        const filtered = this._filterByTab(this._activeTab);

        if (filtered.length === 0) {
            this._view.empty.removeClass('hidden');
            return;
        }
        this._view.empty.addClass('hidden');

        const isGrid = this._activeTab === 'image' || this._activeTab === 'voicephoto' || this._activeTab === 'video';
        grid.classList.toggle('media-gallery-grid--list', !isGrid);
        grid.classList.toggle('media-gallery-grid--tiles', isGrid);

        for (const item of filtered) {
            grid.appendChild(this._renderItem(item));
        }
    }

    _filterByTab(tab) {
        if (tab === 'audio') {
            return this._media.filter(m =>
                m.mediaType === 'music' || (!m.mediaType && m.audioUrl)
            );
        }
        return this._media.filter(m => m.mediaType === tab);
    }

    _renderItem(item) {
        const el = document.createElement('div');
        el.className = 'media-gallery-item';

        const tab = this._activeTab;
        const url = item.mediaUrl || item.audioUrl || '';
        const dateStr = this._formatDate(item.timestamp);

        if (tab === 'image') {
            el.innerHTML = `<img alt="" loading="lazy" class="media-gallery-thumb">
                            <span class="media-gallery-date">${dateStr}</span>`;
            this._setCachedSrc(el.querySelector('img'), url);
            el.addEventListener('click', () => this._openMediaViewer('image', url));

        } else if (tab === 'voicephoto') {
            el.innerHTML = `<img alt="" loading="lazy" class="media-gallery-thumb">
                            <div class="media-gallery-vp-badge">
                                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </div>
                            <span class="media-gallery-date">${dateStr}</span>`;
            this._setCachedSrc(el.querySelector('img'), url);
            el.addEventListener('click', () => this._openVoicephotoViewer(item));

        } else if (tab === 'video') {
            const thumbUrl = item.mediaThumbnailUrl || '';
            if (thumbUrl) {
                // Server-generiertes Poster (ffmpeg first frame) — kleines JPG statt ganzes MP4.
                el.innerHTML = `<img alt="" loading="lazy" class="media-gallery-thumb">
                                <svg class="media-gallery-play media-gallery-play--tile" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                <span class="media-gallery-date">${dateStr}</span>`;
                this._setCachedSrc(el.querySelector('img'), thumbUrl);
            } else {
                // E2E-Video oder altes Video ohne Server-Thumbnail → Placeholder mit zentriertem Play-Icon.
                el.innerHTML = `<div class="media-gallery-video-placeholder">
                                    <svg viewBox="0 0 24 24" width="36" height="36" fill="rgba(255,255,255,0.7)"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                </div>
                                <span class="media-gallery-date">${dateStr}</span>`;
            }
            el.addEventListener('click', () => this._openVideoViewer(item));

        } else if (tab === 'audio') {
            const isMusic = item.mediaType === 'music';
            const title = isMusic
                ? (item.mediaFileName ? this._esc(item.mediaFileName) : t('chatMusic'))
                : t('chatVoiceMessage');
            const dur = item.audioDurationMs ? this._formatDuration(item.audioDurationMs) : '';
            const iconClass = isMusic ? 'media-gallery-music-icon' : 'media-gallery-voice-icon';
            const iconSvg = isMusic
                ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>'
                : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>';
            const audioSrc = isMusic ? url : (item.audioUrl || '');

            el.classList.add('media-gallery-item--list');
            el.innerHTML = `<div class="${iconClass}">${iconSvg}</div>
                            <div class="media-gallery-meta">
                                <span class="media-gallery-meta-title">${title}${dur ? ' (' + dur + ')' : ''}</span>
                                <span class="media-gallery-meta-sub">${dateStr}${item.mediaSizeBytes ? ' · ' + this._formatSize(item.mediaSizeBytes) : ''}</span>
                            </div>`;
            el.addEventListener('click', () => this._openMediaViewer('audio', audioSrc, null, title));
        }

        return el;
    }

    // ── Media Viewer (Lightbox mit Steuerung) ────────────────

    async _openMediaViewer(type, url, extraAudioUrl, title) {
        this._stopActivePlayer();
        const existing = document.querySelector('.media-viewer-overlay');
        if (existing) existing.remove();

        // URL aus Cache holen falls verfügbar
        const cachedUrl = VCS.mediaCache ? (await VCS.mediaCache.get(url).catch(() => url)) : url;

        const overlay = document.createElement('div');
        overlay.className = 'media-viewer-overlay';

        const closeBtn = `<button class="media-viewer-close" type="button"><svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`;

        if (type === 'image') {
            overlay.innerHTML = `${closeBtn}<img src="${this._esc(cachedUrl)}" alt="" class="media-viewer-img">`;
        } else if (type === 'video') {
            overlay.innerHTML = `${closeBtn}<video src="${this._esc(cachedUrl)}" class="media-viewer-video" controls autoplay playsinline webkit-playsinline></video>`;
            this._activePlayer = overlay.querySelector('video');
        } else if (type === 'audio') {
            overlay.innerHTML = `${closeBtn}
                <div class="media-viewer-audio-card">
                    <div class="media-viewer-audio-icon">
                        <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                    </div>
                    <div class="media-viewer-audio-title">${title || ''}</div>
                    <audio src="${this._esc(cachedUrl)}" class="media-viewer-audio" controls autoplay></audio>
                </div>`;
            this._activePlayer = overlay.querySelector('audio');
        }

        this._attachViewerEvents(overlay);
        document.body.appendChild(overlay);
    }

    // ── Video Viewer (nutzt den Chat-Videoplayer mit Karaoke) ─

    async _openVideoViewer(item) {
        this._stopActivePlayer();
        const existing = document.querySelector('.media-viewer-overlay');
        if (existing) existing.remove();

        const chatId = VCS.state.chatActiveChatId;
        const rawUrl = item.mediaUrl || '';
        // Bei nicht-E2E die Server-URL direkt an den Player geben (genau wie der Chat in
        // chat-messages.js:356 — `mediaSrc = msg.mediaUrl`). Das verhindert den Owner-
        // Konflikt mit VCS.mediaCache: der Player wuerde sonst beim Close eine geteilte
        // Blob-URL revoken (isBlob=true) und der zweite Klick stuende vor einer toten URL
        // (errCode=4 MEDIA_ERR_SRC_NOT_SUPPORTED). Nur E2E braucht den Decrypt-Cache.
        const playUrl = item.encrypted
            ? (VCS.mediaCache ? (await VCS.mediaCache.get(rawUrl).catch(() => rawUrl)) : rawUrl)
            : rawUrl;

        // Karaoke-Lookup in chat-video.js liest VCS.state.chatMessages[chatId] und sucht
        // die Message ueber ihre ID. Wenn die Galerie eine Video-Message zeigt, die
        // (noch) nicht im Messages-Cache des Chats ist, schieben wir einen leichten Stub
        // rein – nur mit den Feldern, die videoKaraoke() braucht. Beim Schliessen wird
        // der Stub wieder entfernt, damit nichts an der Chat-Liste haengen bleibt.
        let stubInserted = false;
        if (chatId) {
            if (!VCS.state.chatMessages[chatId]) VCS.state.chatMessages[chatId] = [];
            const msgs = VCS.state.chatMessages[chatId];
            const existingMsg = msgs.find(m => m.id === item.id);
            if (!existingMsg) {
                msgs.push({
                    id: item.id,
                    senderPhone: item.senderPhone,
                    senderLang: item.senderLang,
                    mediaType: 'video',
                    mediaUrl: rawUrl,
                    mediaFileName: item.mediaFileName,
                    translatedVideoUrl: item.translatedVideoUrl,
                    translatedVideoLang: item.translatedVideoLang,
                    encrypted: item.encrypted,
                    _videoWords: item._videoWords,
                    _videoWordsTranslated: item._videoWordsTranslated,
                    _videoTargetLang: item._videoTargetLang,
                    _vcsGalleryStub: true
                });
                stubInserted = true;
            }
        }

        // Toggle-Sichtbarkeit: nur wenn es eine Uebersetzung in anderer Sprache gibt,
        // konsistent mit chat-messages.js (translateToggleHtml-Logik dort).
        const hasTranslatedVideo = !!item.translatedVideoUrl;
        const isSameLangCaption = hasTranslatedVideo && item.translatedVideoLang === item.senderLang;
        const showToggle = hasTranslatedVideo && !isSameLangCaption;

        // Default-Variante: bei neuen Videos (mit Karaoke-Words) bestimmt _preferTranslatedVideo
        // den Default; sonst hat die Bubble in chat-messages.js eine eigene Logik. Hier
        // initial: wenn cross-lang-Uebersetzung vorhanden, Default auf "uebersetzt".
        const isMine = item.senderPhone === VCS.state.chatMyPhone;
        const hasVideoWords = !!(item._videoWords && item._videoWords.length);
        let showTranslatedDefault;
        if (hasVideoWords) {
            showTranslatedDefault = isMine ? false : true;
        } else {
            showTranslatedDefault = showToggle;
        }

        const toggleHtml = showToggle ? `
            <div class="chat-video-toggle-row">
                <button class="chat-video-toggle-btn ${!showTranslatedDefault ? 'chat-video-toggle-btn--active' : ''}"
                        type="button" data-gallery-action="video-variant-orig">${this._esc(t('videoOriginal'))}</button>
                <button class="chat-video-toggle-btn ${showTranslatedDefault ? 'chat-video-toggle-btn--active' : ''}"
                        type="button" data-gallery-action="video-variant-trans">${this._esc(t('videoTranslated'))}</button>
            </div>` : '';

        const overlay = document.createElement('div');
        overlay.className = 'media-viewer-overlay media-viewer-overlay--video';
        overlay.innerHTML = `
            <button class="media-viewer-close" type="button">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
            <div class="media-viewer-video-mount"></div>
            <div class="media-viewer-video-actions">
                ${toggleHtml}
                <button class="chat-media-save-btn" type="button"
                        title="${this._esc(t('saveVideo'))}"
                        aria-label="${this._esc(t('saveVideo'))}"
                        data-gallery-action="save-video">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 3v12"></path>
                        <path d="M7 10l5 5 5-5"></path>
                        <path d="M4 20h16"></path>
                    </svg>
                </button>
            </div>`;
        document.body.appendChild(overlay);

        const cleanup = () => {
            overlay.remove();
            if (stubInserted && chatId) {
                const msgs = VCS.state.chatMessages[chatId] || [];
                const idx = msgs.findIndex(m => m.id === item.id && m._vcsGalleryStub);
                if (idx !== -1) msgs.splice(idx, 1);
            }
            document.removeEventListener('keydown', onKey);
        };
        const onKey = (e) => { if (e.key === 'Escape') cleanup(); };

        overlay.querySelector('.media-viewer-close').addEventListener('click', cleanup);
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(); });
        document.addEventListener('keydown', onKey);

        const mount = overlay.querySelector('.media-viewer-video-mount');
        if (VCS.chatVideo?.playVideo) {
            // revokeBlobOnDetach=false: bei E2E-Videos gehoert die Blob-URL dem
            // VCS.mediaCache (shared), nicht dem Player. Wuerde sonst beim Close revoked,
            // und der zweite Klick auf dasselbe Video bekaeme eine tote URL (errCode=4).
            VCS.chatVideo.playVideo(mount, playUrl, item.id, true, item.mediaDurationMs || null, item.mediaThumbnailUrl || null, false);
        } else {
            // Fallback: einfacher Player, falls VCS.chatVideo nicht verfuegbar ist
            mount.innerHTML = `<video src="${this._esc(playUrl)}" class="media-viewer-video" controls autoplay playsinline></video>`;
            this._activePlayer = mount.querySelector('video');
        }

        // Action-Container = das Overlay selbst; enthaelt Toggle-Buttons + Save-Button.
        // setVideoVariant bekommt das Overlay als scopeEl, damit es die Toggle-Buttons
        // hier findet (statt in der Chat-Bubble).
        const actionsEl = overlay.querySelector('.media-viewer-video-actions');
        if (actionsEl) {
            actionsEl.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-gallery-action]');
                if (!btn) return;
                e.stopPropagation();
                const action = btn.dataset.galleryAction;
                if (action === 'video-variant-orig') {
                    VCS.chatVideo?.setVideoVariant?.(item.id, false, overlay);
                } else if (action === 'video-variant-trans') {
                    VCS.chatVideo?.setVideoVariant?.(item.id, true, overlay);
                } else if (action === 'save-video') {
                    VCS.chatVideo?.saveVideoMessage?.(item.id);
                }
            });
        }
    }

    // ── Voicephoto Viewer mit Karaoke ────────────────────────

    async _openVoicephotoViewer(item) {
        this._stopActivePlayer();
        const existing = document.querySelector('.media-viewer-overlay');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.className = 'media-viewer-overlay';

        const rawImgUrl = item.mediaUrl || '';
        const rawAudioUrl = item.audioUrl || '';
        const imgUrl = VCS.mediaCache ? (await VCS.mediaCache.get(rawImgUrl).catch(() => rawImgUrl)) : rawImgUrl;
        const audioUrl = VCS.mediaCache ? (await VCS.mediaCache.get(rawAudioUrl).catch(() => rawAudioUrl)) : rawAudioUrl;

        // Wörter auswählen (übersetzt wenn passend, sonst original)
        const useTranslated = item._vpWordsTranslated && item._vpTargetLang
            && item._vpTargetLang === (VCS.state.currentLang || 'de')
            && item.senderPhone !== VCS.state.chatMyPhone;
        const words = useTranslated ? item._vpWordsTranslated : item._vpWords;
        const hasKaraoke = words && words.length > 0;

        const closeBtn = `<button class="media-viewer-close" type="button"><svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>`;

        overlay.innerHTML = `${closeBtn}
            <div class="media-viewer-vp-frame">
                <img src="${this._esc(imgUrl)}" alt="" class="media-viewer-vp-img">
                <div class="media-viewer-vp-caption"></div>
                ${audioUrl ? `<div class="media-viewer-vp-controls">
                    <button class="media-viewer-vp-play" type="button">
                        <svg class="media-viewer-vp-icon-play" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><polygon points="6 3 20 12 6 21"/></svg>
                        <svg class="media-viewer-vp-icon-pause hidden" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="5" y="3" width="4" height="18"/><rect x="15" y="3" width="4" height="18"/></svg>
                    </button>
                    <div class="media-viewer-vp-progress-wrap"><div class="media-viewer-vp-progress"><div class="media-viewer-vp-progress-bar"></div></div></div>
                    <span class="media-viewer-vp-dur">0:00</span>
                    <audio src="${this._esc(audioUrl)}"></audio>
                </div>` : ''}
            </div>`;

        const audio = overlay.querySelector('audio');
        const playBtn = overlay.querySelector('.media-viewer-vp-play');
        const playIcon = overlay.querySelector('.media-viewer-vp-icon-play');
        const pauseIcon = overlay.querySelector('.media-viewer-vp-icon-pause');
        const progressBar = overlay.querySelector('.media-viewer-vp-progress-bar');
        const durEl = overlay.querySelector('.media-viewer-vp-dur');
        const captionEl = overlay.querySelector('.media-viewer-vp-caption');

        if (audio) {
            this._activePlayer = audio;

            // Karaoke-Segmente vorbereiten
            let segments = [];
            if (hasKaraoke) {
                let cur = null;
                for (let i = 0; i < words.length; i++) {
                    const w = words[i];
                    const token = w.word || '';
                    if (!token) continue;
                    if (!cur) { cur = { start: w.start, end: w.end, words: [w] }; continue; }
                    cur.end = w.end;
                    cur.words.push(w);
                    // Segment abschliessen bei Satzzeichen oder Sprechpause (> 0.5s)
                    const next = words[i + 1];
                    const gap = next ? next.start - w.end : 0;
                    if (/[.!?]$/.test(token) || gap > 0.3) {
                        segments.push(cur);
                        cur = null;
                    }
                }
                if (cur) segments.push(cur);
            }

            let lastSegIdx = -1;
            let lastWordIdx = -1;

            const karaokeUpdate = () => {
                if (!hasKaraoke || !segments.length) return;
                const ct = audio.currentTime;
                let segIdx = -1;
                for (let i = 0; i < segments.length; i++) {
                    if (ct >= segments[i].start - 0.1 && ct <= segments[i].end + 0.3) { segIdx = i; break; }
                }
                if (segIdx === -1) {
                    for (let i = segments.length - 1; i >= 0; i--) {
                        if (ct >= segments[i].end && ct <= segments[i].end + 1.0) { segIdx = i; break; }
                    }
                }
                if (segIdx === -1) {
                    if (lastSegIdx !== -1) { captionEl.textContent = ''; lastSegIdx = -1; lastWordIdx = -1; }
                    return;
                }
                const seg = segments[segIdx];
                if (segIdx !== lastSegIdx) {
                    captionEl.innerHTML = '';
                    seg.words.forEach((w, i) => {
                        const span = document.createElement('span');
                        span.textContent = (i > 0 ? ' ' : '') + w.word;
                        span.className = 'media-viewer-vp-word';
                        captionEl.appendChild(span);
                    });
                    lastSegIdx = segIdx;
                    lastWordIdx = -1;
                }
                const spans = captionEl.querySelectorAll('.media-viewer-vp-word');
                let activeIdx = -1;
                for (let i = seg.words.length - 1; i >= 0; i--) {
                    if (ct >= seg.words[i].start) { activeIdx = i; break; }
                }
                if (activeIdx !== lastWordIdx) {
                    spans.forEach((s, i) => s.classList.toggle('media-viewer-vp-word--active', i <= activeIdx));
                    lastWordIdx = activeIdx;
                }
            };

            // Fallback: statischer Text wenn keine Wörter vorhanden
            if (!hasKaraoke && item.originalText) {
                captionEl.textContent = item.originalText;
                captionEl.classList.add('media-viewer-vp-caption--static');
            }

            audio.ontimeupdate = () => {
                const dur = isFinite(audio.duration) && audio.duration > 0
                    ? audio.duration
                    : (item.audioDurationMs / 1000 || 0);
                if (dur > 0 && progressBar) {
                    progressBar.style.width = Math.min((audio.currentTime / dur) * 100, 100) + '%';
                }
                if (durEl) {
                    const remaining = dur > 0 ? Math.max(0, (dur - audio.currentTime) * 1000) : 0;
                    durEl.textContent = this._formatDuration(remaining);
                }
                karaokeUpdate();
            };

            audio.onended = () => {
                playIcon.classList.remove('hidden');
                pauseIcon.classList.add('hidden');
                if (progressBar) progressBar.style.width = '0%';
                captionEl.textContent = '';
                lastSegIdx = -1;
                lastWordIdx = -1;
            };

            playBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (audio.paused) {
                    if (audio.ended) audio.currentTime = 0;
                    audio.play().catch(() => {});
                    playIcon.classList.add('hidden');
                    pauseIcon.classList.remove('hidden');
                } else {
                    audio.pause();
                    playIcon.classList.remove('hidden');
                    pauseIcon.classList.add('hidden');
                }
            });

            // Autoplay
            audio.play().then(() => {
                playIcon.classList.add('hidden');
                pauseIcon.classList.remove('hidden');
            }).catch(() => {});
        }

        this._attachViewerEvents(overlay);
        document.body.appendChild(overlay);
    }

    // ── Viewer Events (Close, Escape) ────────────────────────

    // closeViewer() — ein Weg raus fuer alle Faelle: Button, Klick daneben,
    // Escape und die Zurueck-Geste (navigation.js::_handleBackNavigation).
    // Vorher haben Button und Hintergrund-Klick den keydown-Listener stehen
    // lassen — pro geoeffnetem Medium blieb einer haengen.
    closeViewer() {
        if (this._viewerKeyHandler) {
            document.removeEventListener('keydown', this._viewerKeyHandler);
            this._viewerKeyHandler = null;
        }
        this._stopActivePlayer();
        document.querySelector('.media-viewer-overlay')?.remove();
    }

    _attachViewerEvents(overlay) {
        overlay.querySelector('.media-viewer-close')
            .addEventListener('click', () => this.closeViewer());
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.closeViewer();
        });
        this._viewerKeyHandler = (e) => {
            if (e.key === 'Escape') this.closeViewer();
        };
        document.addEventListener('keydown', this._viewerKeyHandler);
    }

    _stopActivePlayer() {
        if (this._activePlayer) {
            this._activePlayer.pause();
            this._activePlayer.removeAttribute('src');
            this._activePlayer.load();
            this._activePlayer = null;
        }
        if (this._karaokeRaf) {
            cancelAnimationFrame(this._karaokeRaf);
            this._karaokeRaf = null;
        }
    }

    // ── Media-Cache Integration ──────────────────────────────

    /**
     * Setzt src auf einem Element über den MediaCache (Cache-Hit = Blob-URL,
     * Cache-Miss = fetch + cachen). Kein direkter Server-Request vom Browser.
     */
    _setCachedSrc(el, url) {
        if (!el || !url) return;
        if (VCS.mediaCache) {
            VCS.mediaCache.get(url).then(cached => {
                el.src = cached;
            }).catch(() => {
                el.src = url;
            });
        } else {
            el.src = url;
        }
    }

    // ── Hilfsfunktionen ──────────────────────────────────────

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }

    _formatDate(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        const now = new Date();
        const pad = n => String(n).padStart(2, '0');
        const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;

        if (d.toDateString() === now.toDateString()) return time;

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) return `${t('chatYesterday')}, ${time}`;

        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${time}`;
    }

    _formatSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    }

    _formatDuration(ms) {
        const sec = Math.round(ms / 1000);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    }
};
